import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Autocomplete,
  Card,
  Paper,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { FileDownload as DownloadIcon } from "@mui/icons-material";
import axiosInstance from "../components/AxiosInstance";
import useSharedLink from "../scenes/SharedLink";
import { useSnackbar } from "../components/SnackBarProvider";
import { usePermissions } from "../Phase3/CheckPermission";
import {
  CLIENT,
  CONSENT_PARTY,
  PROGRAM_BOOK_TEMPLATE,
  PROGRESS_REPORT,
  DOCUMENT_TYPE_OPTIONS,
  READ,
  PROGRESS_REPORT_TEMPLATE,
  dataGridStyle,
  themeStyles,
} from "../constants";

const ProgressReportPage = () => {
  const { checkPermission, giveClients, giveClientsForGuardian } =
    usePermissions();
  const sharedLink = useSharedLink();
  const { openSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [userRole, setUserRole] = useState(localStorage.getItem("role"));
  const [userId] = useState(localStorage.getItem("userId"));
  const [hasPermission, setHasPermission] = useState(true);
  const [documentType, setDocumentType] = useState(null);
  const [formData, setFormData] = useState({ clientName: null });
  const [documentTypeOptions, setDocumentTypeOptions] = useState(
    DOCUMENT_TYPE_OPTIONS
  );

  useEffect(() => {
    //  setHasPermission(userRole !== CLIENT);
    console.log(documentTypeOptions, "document type");
    if (checkPermission(PROGRESS_REPORT_TEMPLATE, READ)) {
      setDocumentTypeOptions((prevOptions) => [
        ...prevOptions,
        "Progress Report Template",
      ]);
    }

    if (userRole === CONSENT_PARTY || userRole === CLIENT) {
      const clientId = localStorage.getItem("clientId");
      if (clientId) fetchClients();
      else fetchProgressReports(userId);
    } else {
      setClients(giveClients(PROGRESS_REPORT, READ));
    }
  }, []);
  const fetchClients = async () => {
    const fetchedClients = await giveClientsForGuardian();
    // if (fetchClients && fetchClients.length > 1) setHasPermission(true);
    setClients(fetchedClients);
  };
  const handleDownload = (fileName) => {
    axiosInstance
      .post(
        sharedLink + "/downloadfile",
        { fileName },
        { responseType: "blob" }
      ) // Pass fileName in the request body
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch((error) => {
        console.error("Error downloading file:", error);
        openSnackbar("Error downloading file: " + error.message, "error");
      });
  };
  const fetchProgressReports = (clientId, documentType = "Progress Report") => {
    const requestBody = {
      clientId: clientId,
      fileCategory: documentType,
    };

    axiosInstance
      .post(sharedLink + "/getdocuments", requestBody)
      .then((response) => {
        if (
          response &&
          Array.isArray(response.data) &&
          response.data.length <= 0
        )
          openSnackbar(
            "No " + (documentType ? documentType : "Reports") + " Uploaded Yet",
            "info"
          );
        setRows(response.data);
      })
      .catch((error) => {
        console.error("Error fetching documents:", error);
        openSnackbar("Error fetching progress reports.", "error");
      });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!hasPermission) {
      fetchProgressReports(userId, documentType);
      return;
    }
    if (!formData.clientName) {
      openSnackbar("Client Name is required", "error");
      return;
    }
    if (!documentType) {
      openSnackbar("Document type is required", "error");
      return;
    }
    fetchProgressReports(formData.clientName.id, documentType);
  };

  const handleClientChange = (event, newValue) => {
    setFormData((prevData) => ({ ...prevData, clientName: newValue }));
  };
  const handleDocumentTypeChange = (event, newValue) => {
    setDocumentType(newValue);
  };

  const columns = [
    { field: "clientName", headerName: "Client Name", flex: 1 },
    { field: "dateOfBirth", headerName: "Date of Birth", flex: 1 },
    { field: "insuranceName", headerName: "Insurance Name", flex: 1 },
    {
      field: "fileName",
      headerName: "File Name",
      flex: 1,
    },
    {
      field: "",
      headerName: "Download File",
      flex: 1,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={() => handleDownload(params.row.fileName)}
          sx={{ textTransform: "none", fontWeight: "bold" }}
        >
          Download
        </Button>
      ),
    },
  ];

  return (
    <Box
      sx={{
        width: "85%",
        mt: 4,
        ml: "auto",
        mr: "auto",
        textAlign: "center",
        backgroundColor: "#f9f9f9",
        borderRadius: 2,
        p: 3,
      }}
    >
      {/* Page Title */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: "bold",
          color: "#3A57E8",
          mb: 3,
          textTransform: "uppercase",
        }}
      >
        Reports
      </Typography>

      {/* Show form only if the user has permission */}

      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: "#ffffff",
          boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
        }}
      >
        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2, // Spacing between elements
              width: "100%",
              justifyContent: "center", // Center-align when Client Name is hidden
            }}
          >
            {/* Client Name Dropdown (Only show if user has permission) */}
            {hasPermission && (
              <Autocomplete
                fullWidth
                options={clients}
                getOptionLabel={(option) =>
                  option.firstName + " " + option.lastName
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      userRole === CLIENT || userRole === CONSENT_PARTY
                        ? "Select Learner *" // ✅ For CLIENT & CONSENT_PARTY
                        : "Select Client *" // ✅ For all other users
                    }
                    sx={{ ...themeStyles.autocompleteStyles }}
                    variant="outlined"
                  />
                )}
                value={formData.clientName}
                onChange={handleClientChange}
                sx={{ flex: 1, minWidth: "250px", maxWidth: "500px" }} // Ensures proper width
              />
            )}

            {/* Document Type Dropdown */}
            <Autocomplete
              fullWidth
              options={documentTypeOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Document Type *"
                  variant="outlined"
                  sx={{ ...themeStyles.autocompleteStyles }}
                />
              )}
              value={documentType}
              onChange={handleDocumentTypeChange}
              sx={{
                flex: hasPermission ? 1 : "unset", // Shrinks when Client Name is hidden
                minWidth: "250px", // Sets a fixed width
                maxWidth: "500px", // Prevents excessive stretching
              }}
            />

            {/* Search Button */}
            <Button
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: "#3A57E8",
                color: "white",
                borderRadius: "40px",
                px: 2,
                py: 1,
                fontSize: "0.9rem",
                minWidth: "100px",
                height: "36px",
                "&:hover": { backgroundColor: "#2A45C2" },
              }}
            >
              Search
            </Button>
          </Box>
        </form>
      </Paper>

      {/* DataGrid for displaying progress reports */}
      <Card
        elevation={3}
        sx={{
          p: 2,
          borderRadius: 2,
          boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
        }}
      >
        <DataGrid
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } }, // ✅ Default 25 rows per page
          }}
          rows={rows}
          columns={columns}
          pageSize={5}
          disableSelectionOnClick
          sx={{
            ...dataGridStyle,
            "& .MuiDataGrid-footerContainer": {
              justifyContent: "space-between", // ✅ Proper alignment
              padding: "10px 20px", // ✅ Adds padding for spacing
              backgroundColor: "#3A57E8", // ✅ Matching footer background
              display: "flex", // ✅ Uses flexbox for better alignment
              alignItems: "center", // ✅ Ensures everything is centered properly
            },
            "& .MuiTablePagination-toolbar": {
              display: "flex",
              justifyContent: "space-between",
              width: "100%", // ✅ Ensure it takes full width
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                color: "#fff", // ✅ White text for "Rows per page" and pagination details
                fontSize: "14px", // ✅ Improve readability
                marginBottom: "2px", // ✅ Fix slight misalignment
                color: "#fff", // ✅ White color for pagination buttons
              },
            "& .MuiTablePagination-actions button": {
              color: "#fff", // ✅ White color for pagination buttons
            },
            "& .MuiTablePagination-root .MuiSvgIcon-root": {
              color: "#fff", // ✅ White color for dropdown icon
            },
            "& .MuiSelect-select": {
              color: "#fff !important", // ✅ Makes the dropdown text white
              fontSize: "14px !important", // ✅ Increases font size
              fontWeight: "bold", // ✅ Makes it slightly bolder for better visibility
              marginTop: "3px",
            },
            "& .MuiOutlinedInput-input": {
              color: "#fff !important", // ✅ Ensures white text inside the dropdown
            },
            "& .MuiSelect-icon": {
              color: "#fff !important", // ✅ Makes the dropdown arrow white
            },
            "& .MuiDataGrid-selectedRowCount": {
              color: "#fff !important", // ✅ Changes "1 row selected" text to white
              fontSize: "14px", // ✅ Slightly bigger for better readability
            },
          }}
        />
      </Card>
    </Box>
  );
};

export default ProgressReportPage;
